import { memo, useState } from "react";
import { Group, NumberInput, Select, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import dayjs from "dayjs";

import NameEdit from "../../NameEdit/NameEdit";
import Formula from "../../Formula/Formula";
import MoreActions from "./MoreActions";
import FieldFunctionWrapper from "./FieldFunctionWrapper";
import {
  DRAG_CONDITION_FIELD,
  DROP_CONDITION_RIGHT_FIELD,
  conditionRightFieldId,
} from "../../../dnd/kinds";
import { useSelectIsFieldHovered } from "../../../../../redux/features/queryBuilder/queryBuilderSlice";
import {
  useUpdateFieldMutation,
  useDeleteFieldMutation,
} from "../../../hooks/mutations";
import { buildFieldPutData } from "../../../utils/conditionField";
import classes from "./Field.module.scss";

const DATE_INTERVAL_ITEMS = [
  { label: "Days before now", value: "DAY_BEFORE_NOW" },
  { label: "Years before now", value: "YEAR_BEFORE_NOW" },
  { label: "Months before now", value: "MONTH_BEFORE_NOW" },
  { label: "Day of month", value: "DAY_OF_MONTH" },
  {
    label: "First day of month before",
    value: "FIRST_DAY_OF_MONTH_BEFORE_NOW",
  },
];

/**
 * Right side of a condition row. Five variants depending on `rightPart.type`:
 *   - FIELD: drop target for entity fields (default for entity-vs-entity comparisons)
 *   - FORMULA: nested Formula editor
 *   - VALUE: free text input
 *   - BOOL: Yes/No select
 *   - DATE: relative date interval editor
 *
 * Drop handling is centralized — the right field only registers a
 * useDroppable for the FIELD case.
 */
const RightField = memo(function RightField({
  ruleId,
  queryId,
  groupId,
  forJoin,
  fieldsData,
  canEdit = true,
}) {
  const [nameEditMode, setNameEditMode] = useState(false);
  const fieldData = fieldsData?.rightPart;
  const entityData = fieldData?.entityField;
  const savedFunctions = entityData?.entityFieldFunction;
  const fieldType = fieldData?.type;
  const leftType = fieldsData?.leftPart?.entityField?.field?.type;

  // EntityField is the only variant that participates in dnd. We still
  // register the droppable from here so the parent ref placement is stable.
  const dropId = conditionRightFieldId({
    queryId,
    groupId,
    fieldId: fieldsData?.id,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropId,
    data: {
      kind: DROP_CONDITION_RIGHT_FIELD,
      fieldsData,
      queryId,
      groupId,
      forJoin,
    },
    disabled: fieldType !== "FIELD" && fieldType !== "FORMULA",
  });

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } =
    useDraggable({
      id: `condition-right-field-drag::${fieldsData?.id}`,
      data: {
        kind: DRAG_CONDITION_FIELD,
        entityField: entityData,
        fieldsData,
        side: "right",
        queryId,
        groupId,
        forJoin,
      },
      disabled: !canEdit || fieldType !== "FIELD" || !entityData,
    });

  const setNodeRef = (node) => {
    setDropRef(node);
    setDragRef(node);
  };

  const dragHandleProps =
    fieldType === "FIELD" && canEdit && entityData
      ? { ...attributes, ...listeners }
      : {};

  const sliceHovered = useSelectIsFieldHovered({
    queryId,
    conditionGroupId: groupId,
    fieldId: fieldsData?.id,
    side: "right",
  });
  const showDropHint = (isOver || sliceHovered) && fieldType === "FIELD";

  let showFunctionWrapper =
    savedFunctions && fieldType === "FIELD" && !nameEditMode;
  if (leftType === "BOOL" || fieldData?.sign) {
    showFunctionWrapper = false;
  }

  let inner = null;
  switch (fieldType) {
    case "FORMULA": {
      const dropField = (
        <EntityDropVisual
          setNodeRef={setNodeRef}
          hovered={showDropHint}
          value={entityData?.name || entityData?.field?.name}
          canEdit={canEdit}
          dragHandleProps={dragHandleProps}
          isDragging={isDragging}
        />
      );
      inner = (
        <Formula
          ruleId={ruleId}
          fieldsData={fieldsData}
          left={false}
          canEdit={canEdit}
          leadingSlot={
            nameEditMode ? (
              <NameEdit
                ruleId={ruleId}
                id={entityData?.id}
                defaultValue={entityData?.name || entityData?.field?.name}
                onDone={() => setNameEditMode(false)}
              />
            ) : savedFunctions ? (
              <FieldFunctionWrapper
                ruleId={ruleId}
                fieldsData={fieldsData}
                functionData={savedFunctions}
                content={dropField}
                id={entityData?.id}
                canEdit={canEdit}
                isRightField
              />
            ) : (
              dropField
            )
          }
        />
      );
      break;
    }
    case "FIELD": {
      inner = nameEditMode ? (
        <NameEdit
          ruleId={ruleId}
          id={entityData?.id}
          defaultValue={entityData?.name || entityData?.field?.name}
          onDone={() => setNameEditMode(false)}
        />
      ) : (
        <EntityDropVisual
          setNodeRef={setNodeRef}
          hovered={showDropHint}
          value={entityData?.name || entityData?.field?.name}
          canEdit={canEdit}
          dragHandleProps={dragHandleProps}
          isDragging={isDragging}
        />
      );
      break;
    }
    case "VALUE":
      inner = (
        <ValueInput ruleId={ruleId} fieldsData={fieldsData} canEdit={canEdit} />
      );
      break;
    case "BOOL":
      inner = (
        <BoolSelect ruleId={ruleId} fieldsData={fieldsData} canEdit={canEdit} />
      );
      break;
    case "DATE":
      inner = (
        <DateIntervalField
          ruleId={ruleId}
          fieldsData={fieldsData}
          canEdit={canEdit}
        />
      );
      break;
    default:
      inner = null;
  }

  return (
    <Group gap={6} align="center" wrap="nowrap" className={classes.fieldWrap}>
      {showFunctionWrapper ? (
        <FieldFunctionWrapper
          ruleId={ruleId}
          fieldsData={fieldsData}
          functionData={savedFunctions}
          content={inner}
          id={entityData?.id}
          canEdit={canEdit}
          isRightField
        />
      ) : (
        inner
      )}
      {leftType !== "BOOL" && canEdit && (
        <MoreActions
          ruleId={ruleId}
          fieldsData={fieldsData}
          id={entityData?.id}
          setNameEditMode={setNameEditMode}
          showFunctionsPopover={
            !savedFunctions && fieldType !== "VALUE" && !!entityData
          }
          fieldType={fieldType}
          showChangeAlias={fieldType === "FIELD" && !!entityData}
          isRightField
          allowValueEntry={leftType !== "DATE"}
          allowDateEntry={leftType === "DATE"}
        />
      )}
    </Group>
  );
});

function EntityDropVisual({
  setNodeRef,
  hovered,
  value,
  canEdit,
  dragHandleProps = {},
  isDragging = false,
}) {
  if (!value || hovered) {
    return (
      <div ref={setNodeRef} className={classes.dropTargetActive}>
        <Text size="sm" c="dimmed" ta="center">
          {canEdit ? "Drop entity here" : ""}
        </Text>
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      className={classes.fieldHolder}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      {...dragHandleProps}
    >
      <TextInput
        value={value}
        disabled
        variant="filled"
        readOnly
        styles={{
          input: {
            cursor: dragHandleProps.onPointerDown ? "grab" : undefined,
          },
        }}
      />
    </div>
  );
}

function ValueInput({ ruleId, fieldsData, canEdit }) {
  const fieldData = fieldsData?.rightPart;
  const entityData = fieldData?.entityField;
  const defaultValue = !fieldData?.sign ? fieldData?.value || "" : "";

  const { mutate: updateField } = useUpdateFieldMutation({ ruleId });
  const { mutate: deleteField } = useDeleteFieldMutation({ ruleId });

  const save = (value) => {
    if (entityData?.id) {
      deleteField({
        entityFieldId: entityData.id,
        left: false,
        conditionField: fieldsData.id,
      });
    }
    updateField({
      ...buildFieldPutData(fieldsData),
      value,
      sign: null,
      type: "VALUE",
    });
  };

  return (
    <TextInput
      key={fieldsData?.operator?.value}
      disabled={!canEdit}
      autoFocus
      placeholder="Enter value"
      defaultValue={defaultValue}
      onBlur={(ev) => save(ev.currentTarget.value)}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") ev.currentTarget.blur();
      }}
      w="100%"
    />
  );
}

function BoolSelect({ ruleId, fieldsData, canEdit }) {
  const { mutate: updateField } = useUpdateFieldMutation({ ruleId });
  const currentValue =
    fieldsData?.rightPart?.value === "true" ||
    fieldsData?.rightPart?.value === true
      ? "Yes"
      : "No";

  const save = (label) => {
    if (label == null) return;
    updateField({
      ...buildFieldPutData(fieldsData),
      value: label === "Yes" ? true : false,
      type: "BOOL",
    });
  };

  return (
    <Select
      disabled={!canEdit}
      data={["Yes", "No"]}
      value={currentValue}
      onChange={save}
      placeholder=""
      variant="unstyled"
      w={100}
      allowDeselect={false}
    />
  );
}

function DateIntervalField({ ruleId, fieldsData, canEdit }) {
  const fieldData = fieldsData?.rightPart;
  const [conditionInterval, setConditionInterval] = useState(
    fieldData?.conditionInterval || "DAY_BEFORE_NOW",
  );
  const dateDefault =
    fieldData?.conditionInterval === "DAY_OF_MONTH" && fieldData?.value
      ? new Date(fieldData.value)
      : null;
  const numberDefault =
    fieldData?.conditionInterval !== "DAY_OF_MONTH" && fieldData?.value
      ? Number(fieldData.value)
      : null;
  const [numberValue, setNumberValue] = useState(numberDefault);

  const { mutate: updateField } = useUpdateFieldMutation({ ruleId });

  const save = (value, intervalValue) => {
    const putData = { ...buildFieldPutData(fieldsData) };
    if (intervalValue) {
      putData.conditionInterval = intervalValue || conditionInterval;
    }
    putData.value = value ?? null;
    putData.type = "DATE";
    updateField(putData);
  };

  return (
    <Group
      gap={6}
      wrap="nowrap"
      align="flex-end"
      className={classes.fieldHolder}
    >
      {conditionInterval === "DAY_OF_MONTH" ? (
        <DateInput
          disabled={!canEdit}
          defaultValue={dateDefault}
          onChange={(v) => save(v ? dayjs(v).format("YYYY-MM-DD") : null)}
          variant="unstyled"
          w={140}
        />
      ) : (
        <NumberInput
          disabled={!canEdit}
          defaultValue={numberDefault ?? undefined}
          w={60}
          hideControls
          variant="unstyled"
          onBlur={(ev) => {
            const next = ev.currentTarget.value;
            const parsed = next === "" ? null : Number(next);
            setNumberValue(parsed);
            save(parsed);
          }}
        />
      )}
      <Select
        disabled={!canEdit}
        data={DATE_INTERVAL_ITEMS}
        value={conditionInterval || ""}
        onChange={(v) => {
          if (v == null) return;
          setConditionInterval(v);
          if (v !== "DAY_OF_MONTH") {
            save(numberValue, v);
          } else {
            setNumberValue(null);
            save(null, v);
          }
        }}
        placeholder=""
        variant="unstyled"
        w="100%"
        allowDeselect={false}
      />
    </Group>
  );
}

export default RightField;
